import React, { useMemo } from "react";
import WaterDropOutlinedIcon from "@mui/icons-material/WaterDropOutlined";
import { useParams } from "react-router-dom";

import RealTimeCard from "./RealTimeCard";
import { trailingAverage, useRealtimeSeries } from "./useRealtimeSeries";
import {
  baseChartOption,
  chartInk,
  lineSeries,
  metricColor,
  withAlpha,
} from "../charts/chartTheme";
import { EM_DASH, describe, formatNumber } from "../../utils/format";

const NORMAL_FLOOR = 95;
const isValid = (value) => Number.isFinite(value) && value >= 0 && value <= 100;

const SPO2RealTime = () => {
  const { watchId } = useParams();

  const paths = useMemo(
    () => ({
      data: `/Health/${watchId}/realTime/spo2`,
      status: `/Health/${watchId}/realTime/spo2StatusDescription`,
      progress: `/Health/${watchId}/realTime/spo2Progress`,
    }),
    [watchId]
  );

  const { values, samples, status, progress } = useRealtimeSeries(
    watchId,
    paths,
    isValid
  );

  const stats = useMemo(() => describe(values, 0), [values]);

  const buildOption = () => {
    const color = metricColor("spo2");
    const line = lineSeries({
      name: "SpO2",
      data: values,
      color,
      smooth: 0,
      showSymbol: false,
    });

    line.markLine = {
      silent: true,
      symbol: "none",
      label: { show: false },
      data: [
        {
          yAxis: NORMAL_FLOOR,
          lineStyle: { color: withAlpha("#fab219", 0.9), type: [5, 5], width: 1.5 },
          label: {
            show: true,
            formatter: `Normal ≥ ${NORMAL_FLOOR}%`,
            position: "insideEndTop",
            color: chartInk().label,
            fontSize: 11,
          },
        },
      ],
    };

    return baseChartOption({
      categories: samples,
      series: [line],
      xName: "Sample",
      yName: "%",
      yMin: 80,
      yMax: 100,
      tooltipFormatter: (params) => {
        const point = params?.[0];
        if (!point) return "";
        return `Sample ${point.axisValue}<br/><strong>${point.value}%</strong>`;
      },
    });
  };

  return (
    <RealTimeCard
      title="Live blood oxygen"
      icon={<WaterDropOutlinedIcon />}
      accent={metricColor("spo2")}
      exportName="spo2"
      status={status}
      progress={progress}
      hasData={values.length > 0}
      waitingCopy="Hold still while the watch measures oxygen saturation. Samples appear here as they stream in."
      deps={[values]}
      buildOption={buildOption}
      stats={[
        {
          label: "Latest",
          value: values.length ? formatNumber(values[values.length - 1], 0) : EM_DASH,
          unit: "%",
          meta: "Most recent sample",
        },
        {
          label: "Avg (last 30s)",
          value: trailingAverage(values, 30, 0),
          unit: "%",
          meta: "Rolling window",
        },
        { label: "Range", value: stats.range, unit: "%", meta: "This capture" },
        { label: "Samples", value: values.length || EM_DASH, meta: "Received so far" },
      ]}
    />
  );
};

export default SPO2RealTime;
